<script setup>
import { ref, computed, watch, defineProps, defineEmits } from 'vue';
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/vue';
import { ChevronUpDownIcon } from '@heroicons/vue/20/solid';
import { widgetRegistry, getDefaultWidgetConfig, validateWidgetConfig } from '@/components/widgets';
import EventsConfigForm from '@/components/widgets/events/EventsConfigForm.vue';
import GaugeConfigForm from '@/components/widgets/gauge/GaugeConfigForm.vue';
import MapConfigForm from '@/components/widgets/map/MapConfigForm.vue';
import StatsCardConfigForm from '@/components/widgets/statsCard/StatsCardConfigForm.vue';
import StatusConfigForm from '@/components/widgets/status/StatusConfigForm.vue';
import TableConfigForm from '@/components/widgets/table/TableConfigForm.vue';
import TimeseriesConfigForm from '@/components/widgets/timeseries/TimeseriesConfigForm.vue';
import { useModalBackdropClose } from '@/composables/useModalBackdropClose';

const props = defineProps({
  widgetId: {
    type: String,
    default: null
  },
  existingWidget: {
    type: Object,
    default: null
  },
  dataID: {
    type: String,
    required: true
  },
  pageType: {
    type: String,
    default: 'custom' // 'custom', 'timeseries', 'events'
  }
});

const emit = defineEmits(['close', 'save']);

// Form state - determine default type based on page type
const getDefaultType = () => {
  if (props.existingWidget?.type) {
    return props.existingWidget.type;
  }
  if (props.pageType === 'timeseries') return 'timeseries';
  if (props.pageType === 'events') return 'events';
  return 'gauge';
};

const widgetType = ref(getDefaultType());
const widgetTitle = ref(props.existingWidget?.title || '');
const widgetConfig = ref(
  props.existingWidget 
    ? (() => {
        // Deep copy excluding title and type
        const copy = JSON.parse(JSON.stringify(props.existingWidget));
        const { title, type, ...config } = copy;
        console.log(title, type)
        return config;
      })()
    : getDefaultWidgetConfig(getDefaultType()).config
);

// Computed
const isEditMode = computed(() => props.widgetId !== null);
const modalTitle = computed(() => {
  if (isEditMode.value) {
    return props.pageType === 'timeseries' ? 'Edit Chart' : props.pageType === 'events' ? 'Edit Event Table' : 'Edit Widget';
  }
  return props.pageType === 'timeseries' ? 'Add Chart' : props.pageType === 'events' ? 'Add Event Table' : 'Add Widget';
});

// Filter available widgets based on page type
const availableWidgets = computed(() => {
  if (props.pageType === 'timeseries') {
    return widgetRegistry.filter(w => w.type === 'timeseries');
  } else if (props.pageType === 'events') {
    return widgetRegistry.filter(w => w.type === 'events');
  } else {
    return widgetRegistry;
  }
});

// Watch widget type changes to reset config
watch(widgetType, (newType) => {
  const defaultConfig = getDefaultWidgetConfig(newType);
  widgetConfig.value = defaultConfig.config;
});

// Validation
const isValid = computed(() => {
  return validateWidgetConfig(widgetType.value, widgetConfig.value, widgetTitle.value);
});

// Methods
const handleSave = () => {
  if (!isValid.value) return;
  
  const widget = {
    type: widgetType.value,
    title: widgetTitle.value.trim(),
    ...widgetConfig.value
  };
  
  emit('save', { widgetId: props.widgetId, widget });
};

const handleClose = () => {
  emit('close');
};

const { onBackdropMousedown, onBackdropMouseup, onContentMousedown } = useModalBackdropClose(handleClose);
</script>

<template>
  <div class="fixed top-0 left-0 right-0 bottom-0 bg-black/50 flex justify-center items-center z-50" @mousedown.self="onBackdropMousedown" @mouseup.self="onBackdropMouseup">
    <div class="ui-modal bg-mc-white dark:bg-mc-charcoal p-6 rounded-mc-sm w-[700px] max-h-[calc(100vh-50px)] overflow-y-auto shadow-mc-md" @mousedown="onContentMousedown">
      <!-- Header -->
      <div class="h-[3px] w-full bg-mc-red rounded-full mb-5"></div>
      <h2 class="font-extrabold text-xl text-center mb-5 text-mc-ink dark:text-on-dark tracking-tight">{{ modalTitle }}</h2>

      <!-- Body -->
      <div class="space-y-6">
        <!-- Widget Type Selection (only show if multiple options available) -->
        <div v-if="!isEditMode && availableWidgets.length > 1">
          <label for="widget-type" class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5 block">Widget Type <span class="text-mc-red">*</span></label>
          <Listbox v-model="widgetType" as="div" class="relative">
            <ListboxButton class="w-full flex items-center justify-between p-3 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20">
              <span>{{ availableWidgets.find(w => w.type === widgetType)?.label }}</span>
              <ChevronUpDownIcon class="h-4 w-4 text-ink-muted dark:text-on-dark-muted shrink-0" aria-hidden="true" />
            </ListboxButton>
            <ListboxOptions class="absolute z-20 mt-1 w-full bg-mc-white dark:bg-mc-charcoal border border-mc-steel dark:border-line-on-dark rounded-mc-sm shadow-mc-md max-h-60 overflow-auto focus:outline-none">
              <ListboxOption
                v-for="widget in availableWidgets"
                :key="widget.type"
                :value="widget.type"
                v-slot="{ active, selected }"
              >
                <li :class="['px-3 py-2 cursor-pointer select-none text-mc-ink dark:text-on-dark', active ? 'bg-mc-steel/40 dark:bg-white/10' : '', selected ? 'font-semibold' : '']">
                  {{ widget.label }}
                </li>
              </ListboxOption>
            </ListboxOptions>
          </Listbox>
        </div>

        <!-- Widget Title -->
        <div>
          <label for="widget-title" class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5 block">Widget Title <span class="text-mc-red">*</span></label>
          <input
            id="widget-title"
            v-model="widgetTitle"
            type="text"
            placeholder="Enter widget title"
            class="w-full p-3 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark placeholder:text-ink-muted dark:placeholder:text-on-dark-muted focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20"
          />
        </div>

        <!-- Widget-Specific Configuration Forms -->
        <div>
          <h3 class="text-sm font-bold text-center text-mc-ink dark:text-on-dark uppercase tracking-widest mb-4">
            {{ widgetRegistry.find(w => w.type === widgetType)?.label }} Settings
          </h3>

          <!-- Event Log Form -->
          <EventLogConfigForm
            v-if="widgetType === 'eventLog'"
            v-model="widgetConfig"
          />

          <!-- Gauge Form -->
          <GaugeConfigForm
            v-else-if="widgetType === 'gauge'"
            v-model="widgetConfig"
            :dataID="dataID"
            :widgetTitle="widgetTitle"
          />

          <!-- Stats Card Form -->
          <StatsCardConfigForm
            v-else-if="widgetType === 'statsCard'"
            v-model="widgetConfig"
            :dataID="dataID"
          />

          <!-- Map Form -->
          <MapConfigForm
            v-else-if="widgetType === 'map'"
            v-model="widgetConfig"
            :dataID="dataID"
          />

          <!-- Status Form -->
          <StatusConfigForm
            v-else-if="widgetType === 'status'"
            v-model="widgetConfig"
            :dataID="dataID"
            :widgetTitle="widgetTitle"
          />

          <!-- Table Form -->
          <TableConfigForm
            v-else-if="widgetType === 'table'"
            v-model="widgetConfig"
            :dataID="dataID"
          />

          <!-- Timeseries Form -->
          <TimeseriesConfigForm
            v-else-if="widgetType === 'timeseries'"
            v-model="widgetConfig"
            :dataID="dataID"
            :widgetTitle="widgetTitle"
          />

          <!-- Events Form -->
          <EventsConfigForm
            v-else-if="widgetType === 'events'"
            v-model="widgetConfig"
            :dataID="dataID"
            :widgetTitle="widgetTitle"
          />
        </div>
      </div>

      <!-- Footer -->
      <div class="flex justify-between items-center mt-8">
        <button
          @click="handleClose"
          type="button"
          class="px-5 py-2 text-xs rounded-full border border-mc-steel dark:border-white/20 text-ink-muted dark:text-on-dark-muted hover:bg-mc-steel/20 dark:hover:bg-white/10 transition-colors duration-160"
        >
          Cancel
        </button>
        <button
          @click="handleSave"
          type="button"
          :disabled="!isValid"
          :class="[
            isValid 
              ? 'default-button' 
              : 'default-button-off'
          ]"
        >
          {{ isEditMode ? (pageType === 'timeseries' ? 'Update Chart' : pageType === 'events' ? 'Update Table' : 'Update Widget') : (pageType === 'timeseries' ? 'Add Chart' : pageType === 'events' ? 'Add Table' : 'Add Widget') }}
        </button>
      </div>
    </div>
  </div>
</template>
